import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../constants";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Backer-visibility toggle for the Founding Backers wall.
 * Shown only to lifetime users on the Dashboard.
 */
export default function BackerVisibilityCard({ user }) {
  const [visible, setVisible] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [personalCode, setPersonalCode] = useState("");

  useEffect(() => {
    if (!user) return;
    setVisible(Boolean(user.show_on_backers_wall));
    setDisplayName(user.backer_display_name || user.name || "");
  }, [user]);

  useEffect(() => {
    if (!user || user.subscription_status !== "lifetime") return;
    axios
      .get(`${API}/auth/my-invite`, { withCredentials: true, headers: authHeaders() })
      .then((res) => setPersonalCode(res.data.code || ""))
      .catch(() => setPersonalCode(""));
  }, [user]);

  if (!user || user.subscription_status !== "lifetime") return null;

  const inviteUrl = personalCode ? `${window.location.origin}/invite/${personalCode}` : "";
  const copyInvite = () => {
    if (!inviteUrl) return;
    navigator.clipboard.writeText(inviteUrl);
    toast.success("Invite link copied");
  };

  const save = async (nextVisible, nextName = displayName) => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/auth/backer-visibility`,
        { visible: nextVisible, display_name: nextName },
        { withCredentials: true, headers: authHeaders() }
      );
      setVisible(nextVisible);
      toast.success(nextVisible ? "You'll appear on the Backers wall" : "Removed from Backers wall");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-glass p-5" data-testid="backer-visibility-card">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-[#A3E635]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V7m0 10v-1m-6 0a9 9 0 110-18 9 9 0 010 18z" />
          </svg>
          <h3 className="font-heading text-white font-semibold">Founding Backers Wall</h3>
        </div>
        <button
          onClick={() => save(!visible)}
          disabled={saving}
          role="switch"
          aria-checked={visible}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${visible ? "bg-[#A3E635]" : "bg-white/20"}`}
          data-testid="backer-visibility-switch"
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${visible ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>
      <p className="text-[#E8E4DC] text-xs mb-3">
        Show your name on the public Founding Backers wall on our LandingPage.
      </p>
      {visible && (
        <div className="flex gap-2">
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            onBlur={() => save(true, displayName)}
            maxLength={40}
            className="input-dark flex-1 text-sm"
            placeholder="Display name (up to 40 chars)"
            data-testid="backer-display-name"
          />
        </div>
      )}
      <div className="mt-4 pt-4 border-t border-white/10">
        <p className="text-[#E8E4DC] text-xs mb-2">
          <span className="text-[#A3E635] font-semibold">Share the gift of Top Screen&trade;</span> — your personal invite link grants 50% off lifetime, attributed back to you.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={inviteUrl}
            readOnly
            onClick={(e) => e.target.select()}
            className="input-dark flex-1 text-xs font-mono"
            data-testid="backer-invite-url"
          />
          <button onClick={copyInvite} disabled={!inviteUrl} className="btn-secondary text-xs px-4 disabled:opacity-50" data-testid="backer-copy-invite">Copy</button>
        </div>
      </div>
    </div>
  );
}
